import { initMixpanel } from '@analytics/src/mixpanel';
import { isDemoMode } from '@utils/demoMode';
import { useEffect, useCallback } from 'react';
import { E_APP_ENV } from 'src/config-global';

// ----------------------------------------------------------------------

/**
 * Iubenda purpose ids, taken from the cookie solution's own Google Consent Mode map
 * in core-<lang>.js: `analytics_storage -> purposes [4]` and
 * `ad_storage / ad_user_data / ad_personalization -> purposes [5]`. Purposes 1-3 are
 * necessary, functional and experience, and never gate telemetry.
 */
const MEASUREMENT_PURPOSE = 4;
const MARKETING_PURPOSE = 5;

/** Must match the event name dispatched by the consent bridge in index.html. */
const CONSENT_EVENT = 'jooice:consent';

const isDeployedEnv = [E_APP_ENV.DEV, E_APP_ENV.STAGING, E_APP_ENV.PROD].includes(
  import.meta.env.VITE_APP_ENV as E_APP_ENV,
);

/**
 * Bridges the Iubenda Cookie Solution banner to the analytics that ship inside our own
 * bundle. Iubenda's auto-blocker only stops third-party <script> and <iframe> tags, so
 * Mixpanel, an npm dependency compiled into the bundle, stays invisible to it and has
 * to be gated here.
 *
 * The answer arrives from the inline bridge in index.html rather than from the Iubenda
 * callback directly: the widget copies `csConfiguration.callback` into its own options
 * when its core boots, so a callback attached from this effect is dropped whenever the
 * core wins that race. The bridge registers ahead of the widget and re-broadcasts.
 *
 * Consent itself lives in the `_iub_cs-s<siteId>` cookie that the widget writes on
 * `.jooice.com`, so it is shared with www.jooice.com and survives `localStorage.clear()`
 * on logout.
 */
export const useCookieConsent = () => {
  const initMixpanelAnalytics = useCallback(() => {
    if (!isDeployedEnv && !import.meta.env.VITE_MIXPANEL_KEY) {
      return;
    }

    // Route ingestion + replay assets through the first-party reverse proxy
    // (data.jooice.net) so ad-blockers don't drop client events. The renamed
    // routes map to Mixpanel's real endpoints in the proxy's nginx config.
    // Unset (local dev): SDK falls back to api.mixpanel.com directly.
    const proxyHost = import.meta.env.VITE_MIXPANEL_PROXY_HOST;

    initMixpanel(
      import.meta.env.VITE_MIXPANEL_KEY,
      proxyHost
        ? {
            api_host: proxyHost,
            api_routes: { track: 'e', engage: 'p', groups: 'g', record: 'r' },
            recorder_src: `${proxyHost}/recorder.min.js`,
          }
        : undefined,
    );
  }, []);

  const applyConsent = useCallback(
    (purposes?: IubendaPurposes) => {
      // Demo mode runs on fabricated accounts, so its events must never reach the real
      // dashboards, whatever the user answered in the banner.
      if (isDemoMode()) {
        return;
      }

      if (!purposes) {
        return;
      }

      // Start analytics only once the user has granted measurement or marketing.
      if (!purposes[MEASUREMENT_PURPOSE] && !purposes[MARKETING_PURPOSE]) {
        return;
      }

      // initMixpanel is idempotent, so replays of the same preference are free.
      try {
        initMixpanelAnalytics();
      } catch (error) {
        console.error('Error initializing analytics with consent:', error);
      }
    },
    [initMixpanelAnalytics],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // A returning visitor's stored preference fires while the app bundle is still
    // loading, so read what the bridge already recorded before subscribing.
    applyConsent(window.jooiceConsentPurposes ?? undefined);

    const onConsent = (event: Event) => {
      applyConsent((event as CustomEvent<IubendaPurposes | null>).detail ?? undefined);
    };

    window.addEventListener(CONSENT_EVENT, onConsent);

    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, [applyConsent]);
};
